import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Loader2, Mail } from "lucide-react";
import { API, api } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BRAND } from "@/lib/site";
import { gaEvent } from "@/lib/track";

interface Order { payment_status: string; name: string; listing_id: string; amount?: number; access_days: number; email?: string; expires_at?: string; expired?: boolean; files?: { paper: string; label: string; name: string }[] }

function Downloads({ o, token }: { o: Order; token: string }) {
  if (o.expired) return <p className="mt-6 text-[#23324A]/80" data-testid="order-expired">These download links expired {o.access_days} days after purchase. Reply to your receipt email and Kelly will help.</p>;
  return (
    <>
      <div className="mt-8 flex flex-wrap gap-3">
        {o.files?.map((f) => (
          <a key={f.paper} href={`${API}/shop/download/${token}/${f.paper}`} className="btn-solid" data-testid={`order-download-${f.paper}`}><Download className="h-4 w-4" /> {f.label} PDF</a>
        ))}
      </div>
      <p className="mt-6 flex items-start gap-2 text-sm text-[#23324A]/75" data-testid="order-email-note">
        <Mail className="mt-0.5 h-4 w-4 shrink-0" />
        {o.email ? `We also emailed these links to ${o.email}.` : "Bookmark this page to come back to your files."} Links work for {o.access_days} days, as often as you like
        {o.expires_at ? ` (until ${new Date(o.expires_at).toLocaleDateString()})` : ""}.
      </p>
    </>
  );
}

export default function OrderPage() {
  const { token = "" } = useParams();
  const [o, setO] = useState<Order | null | undefined>(undefined);
  const [slow, setSlow] = useState(false);
  usePageMeta(`Your download | ${BRAND}`, "Your printable download.");
  useEffect(() => {
    let tries = 0, stop = false;
    const tick = async () => {
      try {
        const r = await api.post(`/shop/orders/${token}/verify`);
        if (stop) return;
        setO(r.data);
        if (r.data.payment_status === "paid" && !localStorage.getItem(`dgh_ga_${token}`)) {
          localStorage.setItem(`dgh_ga_${token}`, "1");
          gaEvent("purchase", { transaction_id: token.slice(0, 12), currency: "USD", value: r.data.amount, items: [{ item_id: r.data.listing_id, item_name: r.data.name, price: r.data.amount }] });
        }
        if (r.data.payment_status !== "pending") return;
        if (++tries < 15) setTimeout(tick, 2000);
        else setSlow(true);
      } catch { if (!stop) setO(null); }
    };
    tick();
    return () => { stop = true; };
  }, [token]);

  return (
    <div className="mx-auto max-w-2xl px-5 pt-16 sm:px-8" data-testid="order-page">
      {o === undefined || o?.payment_status === "pending" ? (
        <div data-testid="order-pending">
          <Loader2 className="h-7 w-7 animate-spin text-[#D9785C]" />
          <h1 className="mt-4 font-heading text-4xl font-semibold text-[#23324A]">Confirming your payment...</h1>
          <p className="mt-3 text-[#23324A]/75" data-testid="order-pending-note">
            {slow ? "This is taking longer than usual. Check your email for your download links, or refresh this page in a minute." : "This usually takes a few seconds. Please keep this page open."}
          </p>
        </div>
      ) : o === null || o.payment_status !== "paid" ? (
        <div data-testid="order-failed">
          <h1 className="font-heading text-4xl font-semibold text-[#23324A]">We couldn't confirm this payment</h1>
          <p className="mt-3 text-[#23324A]/75">If you were charged, your files will arrive by email shortly. Otherwise you can try again.</p>
          <Link to={o ? `/shop/${o.listing_id}` : "/shop"} className="btn-line mt-6" data-testid="order-retry-link">Back to the printable</Link>
        </div>
      ) : (
        <div data-testid="order-paid">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#23324A]/55">Thank you!</p>
          <h1 className="font-heading text-4xl font-semibold text-[#23324A] sm:text-5xl" data-testid="order-name">{o.name}</h1>
          <p className="mt-3 text-[#23324A]/80">Your printable is ready. Print at 100% scale on cardstock or regular paper.</p>
          <Downloads o={o} token={token} />
          <Link to="/shop" className="mt-10 inline-block text-sm font-semibold text-[#23324A] underline" data-testid="order-shop-link">Browse more printables</Link>
        </div>
      )}
    </div>
  );
}
